import { ChangeEvent, createContext, useState } from 'react'
import * as XLSX from 'xlsx'

import {
  PREVIEW_DATA_COLUMN_MAPPING_LABELS,
  SCROLL_THRESHHOLD,
} from '@/constants'
import { useExcelFileUploaderStore } from '@/stores/import/excelFileUploaderStore'
import type {
  NomenclaturePreviewDataColumnMapping,
  NomenclaturePreviewDataColumnMappingBySheet,
  PreviewDataFromExcelColumnType,
  PreviewDataFromExcelType,
} from '@/types'

import ExcelFilePreviewDataTableBodyCell from '../ExcelFilePreviewDataTable/ExcelFilePreviewDataTableBodyCell/ExcelFilePreviewDataTableCell'
import ExcelFilePreviewDataTableHeadCell from '../ExcelFilePreviewDataTable/ExcelFilePreviewDataTableHeadCell/ExcelFilePreviewDataTableHeadCell'

type ExcelFileUploaderPropsType = {
  children: React.ReactElement
  previewDataColumnMappingProto: NomenclaturePreviewDataColumnMapping
}

export type ExcelFileUploaderContextType = {
  file: Blob | string | null
  previewData: PreviewDataFromExcelType
  visiblePreviewData: PreviewDataFromExcelType
  previewDataColumns: PreviewDataFromExcelColumnType
  includedSheets: string[]
  allSheets: string[]
  setIncludedSheets: (includedSheets: string[]) => void
  handleUploadFile: (e: ChangeEvent<HTMLInputElement>) => void
  handleUploadPreviewData: () => void
  handleScrollPreviewData: (e: React.UIEvent<HTMLElement>) => void
}

export const ExcelFileUploaderContext =
  createContext<null | ExcelFileUploaderContextType>(null)

function ExcelFileUploader(props: ExcelFileUploaderPropsType) {
  const { children, previewDataColumnMappingProto } = props

  const [file, setFile] = useState<Blob | string | null>(null)
  const [includedSheets, setIncludedSheets] = useState<string[]>([])
  const [allSheets, setAllSheets] = useState<string[]>([])
  const [previewData, setPreviewData] = useState<PreviewDataFromExcelType>({})
  const [visiblePreviewData, setVisiblePreviewData] =
    useState<PreviewDataFromExcelType>({})
  const [previewDataColumns, setPreviewDataColumns] =
    useState<PreviewDataFromExcelColumnType>({})

  const {
    currentIncludedSheet,
    setCurrentIncludedSheet,
    setPreviewDataColumnMapping,
  } = useExcelFileUploaderStore((state) => state)

  const handleUploadFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const file = e.target.files[0]
      setFile(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        if (e.target) {
          const workbook = XLSX.read(e.target.result)
          setAllSheets(workbook.SheetNames)
          setIncludedSheets([])
        }
      }
      reader.readAsArrayBuffer(file)
    }
  }

  const handleUploadPreviewData = () => {
    setCurrentIncludedSheet(
      includedSheets.length ? includedSheets[0] : allSheets[0]
    )
    const previewDataColumnMappingProtoBySheet: NomenclaturePreviewDataColumnMappingBySheet =
      {}
    if (includedSheets.length) {
      includedSheets.forEach((sheet) => {
        previewDataColumnMappingProtoBySheet[sheet] =
          previewDataColumnMappingProto
      })
    } else {
      allSheets.forEach((sheet) => {
        previewDataColumnMappingProtoBySheet[sheet] =
          previewDataColumnMappingProto
      })
    }
    setPreviewDataColumnMapping(previewDataColumnMappingProtoBySheet)
    if (file instanceof Blob) {
      const reader = new FileReader()
      reader.onload = (e) => {
        if (e.target) {
          const workbook = XLSX.read(e.target.result)
          const parsedSheets: PreviewDataFromExcelType = {}
          const getParsedSheets = (sheets: string[]) => {
            sheets.forEach((sheetName) => {
              const worksheet = workbook.Sheets[sheetName]
              const ref = worksheet['!ref']
              const range = ref ? XLSX.utils.decode_range(ref) : null
              const colOffset = range ? range.s.c : 0

              const jsonData = XLSX.utils
                .sheet_to_json<Array<string | number | boolean | null>>(
                  worksheet,
                  { header: 1, defval: null }
                )
                .filter((row) =>
                  row.some((cell) => cell !== null && cell !== undefined)
                )
                .map((row, rowIndex) => {
                  const obj: {
                    __rowKey: string
                    [key: number]: string | number | boolean | null
                    [key: string]: string | number | boolean | null
                  } = { __rowKey: `${sheetName}-${rowIndex}` }
                  row.forEach((cell, index) => {
                    obj[colOffset + index + 1] = cell
                  })
                  return obj
                })
              let maxColIndex = 0
              jsonData.forEach((row) => {
                Object.keys(row).forEach((column) => {
                  if (
                    !Number.isNaN(Number(column)) &&
                    Number(column) > maxColIndex
                  ) {
                    maxColIndex = Number(column)
                  }
                })
              })
              const minColIndex = colOffset + 1
              const colCount = maxColIndex - minColIndex + 1
              setPreviewDataColumns((previousPreviewDataColumns) => ({
                ...previousPreviewDataColumns,
                [sheetName]: [
                  {
                    title: '',
                    key: 'empty',
                    fixed: 'left',
                    width: 50,
                    render: (_: undefined, __: undefined, index: number) =>
                      index + 1,
                  },
                  ...Array.from({ length: colCount }, (_, i) => {
                    const colIndex = minColIndex + i
                    return {
                      title: (
                        <ExcelFilePreviewDataTableHeadCell
                          index={colIndex - 1}
                          sheet={sheetName}
                          previewDataColumnMappingOptions={[
                            { value: '', label: '' },
                            ...Object.keys(previewDataColumnMappingProto).map(
                              (key) => ({
                                value: key,
                                label: PREVIEW_DATA_COLUMN_MAPPING_LABELS[key],
                              })
                            ),
                          ]}
                        />
                      ),
                      key: colIndex - 1,
                      dataIndex: colIndex,
                      width: 350,
                      render: (text: string | number | boolean | null) => (
                        <ExcelFilePreviewDataTableBodyCell
                          text={text}
                          i={colIndex - 1}
                        />
                      ),
                    }
                  }),
                ],
              }))
              parsedSheets[sheetName] = jsonData
              return jsonData
            })
          }
          getParsedSheets(includedSheets.length ? includedSheets : allSheets)
          setPreviewData(parsedSheets)
          const visiblePreviewDataObject: PreviewDataFromExcelType = {}
          Object.keys(parsedSheets).forEach((parsedSheet) => {
            visiblePreviewDataObject[parsedSheet] = parsedSheets[
              parsedSheet
            ].slice(0, 50)
          })
          setVisiblePreviewData(visiblePreviewDataObject)
          if (!includedSheets.length) {
            setIncludedSheets(allSheets)
          }
        }
      }
      reader.readAsArrayBuffer(file)
    }
  }

  const handleScrollPreviewData = (e: React.UIEvent<HTMLElement>) => {
    const target = e.target as HTMLElement
    if (target.scrollTop + SCROLL_THRESHHOLD > target.scrollHeight) {
      const visiblePreviewDataObject: PreviewDataFromExcelType = {}
      visiblePreviewDataObject[currentIncludedSheet] = previewData[
        currentIncludedSheet
      ].slice(
        visiblePreviewData[currentIncludedSheet].length,
        visiblePreviewData[currentIncludedSheet].length + 50
      )

      setVisiblePreviewData((prevVisiblePreviewData) => ({
        ...prevVisiblePreviewData,
        [currentIncludedSheet]: [
          ...prevVisiblePreviewData[currentIncludedSheet],
          ...visiblePreviewDataObject[currentIncludedSheet],
        ],
      }))
    }
  }

  return (
    <ExcelFileUploaderContext.Provider
      value={{
        file,
        previewData,
        visiblePreviewData,
        previewDataColumns,
        includedSheets,
        allSheets,
        setIncludedSheets,
        handleUploadFile,
        handleUploadPreviewData,
        handleScrollPreviewData,
      }}
    >
      {children}
    </ExcelFileUploaderContext.Provider>
  )
}

export default ExcelFileUploader
